import { useState } from "react";
import { recordCashMovement } from "../../../core/data/local/shift";
import { money } from "../../../core/domain/money/money";
import { formatPesos, parsePesos, MoneyInputError } from "../money";

/**
 * Cash out of the drawer, at the till (T020, FR-128, research.md R2).
 *
 * THE CORE HAS ALWAYS HAD THIS AND NOTHING EVER CALLED IT. `recordCashMovement`
 * commits locally, queues its envelope, and the shift's expected cash already
 * counts it — so every peso of ice or gas bought from the drawer since opening
 * has shown up only as an unexplained shortfall at close, if at all. This form
 * is the missing button, not a new mechanism: it works offline, syncs once, and
 * the books give the paid-out a category afterwards.
 *
 * ANY STAFF MEMBER ON SHIFT MAY USE IT, because the barista buying ice is the
 * person holding the money. The reason is required, the person is recorded, and
 * the count at close keeps it honest.
 */

export interface CashOutFormProps {
  shiftId: string;
  terminalId: string;
  staffId: string;
  onDone(message: string): void;
  onCancel(): void;
}

export function CashOutForm({ shiftId, terminalId, staffId, onDone, onCancel }: CashOutFormProps) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);

    let centavos: number;
    try {
      centavos = parsePesos(amount);
    } catch (caught) {
      setError(caught instanceof MoneyInputError ? caught.message : "That is not an amount.");
      return;
    }
    if (centavos === 0) {
      setError("Enter how much was taken.");
      return;
    }
    if (reason.trim() === "") {
      setError("Say what the money was for.");
      return;
    }

    setBusy(true);
    try {
      await recordCashMovement({
        shiftId,
        terminalId,
        direction: "out",
        amount: money(centavos),
        reason: reason.trim(),
        staffId,
      });
      onDone(`₱${formatPesos(centavos)} taken from the drawer for ${reason.trim()}.`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not record it");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="books books--form" role="dialog" aria-label="Cash out of the drawer">
      <h2 className="books__title">Cash out</h2>
      <p className="books__hint">
        Money taken from the drawer for the shop — ice, gas, a quick supply run. It comes off the
        drawer's expected cash straight away, so the count at close still matches.
      </p>

      <label className="books__field">
        <span>How much (pesos)</span>
        <input
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          inputMode="decimal"
          placeholder="e.g. 130"
          autoFocus
        />
      </label>

      <label className="books__field">
        <span>What for</span>
        <input
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          maxLength={200}
          placeholder="e.g. Tube ice"
        />
      </label>

      {error && (
        <p className="books__error" role="alert">
          {error}
        </p>
      )}

      <div className="books__buttons">
        <button type="button" className="books__action" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="books__action is-primary" onClick={() => void submit()} disabled={busy}>
          {busy ? "Recording…" : "Record"}
        </button>
      </div>
    </section>
  );
}
